"use client";

import { ReactNode } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";

interface SidebarSectionProps {
  title?: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}

/**
 * Sidebar section with optional header and trailing action.
 * Use inside a sidebar layout to keep consistent rhythm.
 */
export function SidebarSection({ title, children, action, className }: SidebarSectionProps) {
  return (
    <div className={cn("flex flex-col min-h-0", className)}>
      {(title || action) && (
        <div className="flex items-center justify-between px-4 h-9 shrink-0">
          {title && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {title}
            </span>
          )}
          {action}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">{children}</div>
    </div>
  );
}

interface SidebarNavItemProps {
  icon?: ReactNode;
  label: string;
  active?: boolean;
  badge?: number | string;
  onClick?: () => void;
  href?: string;
}

/**
 * Consistent sidebar nav item with active state and optional badge.
 */
export function SidebarNavItem({ icon, label, active, badge, onClick, href }: SidebarNavItemProps) {
  const content = (
    <>
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="flex-1 truncate text-left">{label}</span>
      {badge !== undefined && badge !== 0 && (
        <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground shrink-0">
          {badge}
        </span>
      )}
    </>
  );

  const classes = cn(
    "w-full flex items-center gap-2.5 px-3 h-8 rounded-md text-[13px] transition-all cursor-pointer relative",
    active
      ? "bg-primary/10 text-primary font-medium border-l-2 border-primary pl-2.5"
      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
  );

  if (href) {
    return (
      <Link href={href} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button onClick={onClick} className={classes}>
      {content}
    </button>
  );
}